import React, { useState, useEffect, useContext, useRef } from 'react';
import { BsChevronDown, BsDownload, BsArchive, BsCopy, BsLink45Deg } from 'react-icons/bs';
import { Row, checkAPICallsQuota } from '../../shared/utils';
import KycQuotaModal from '../../shared/KycQuotaModal';
import AddUserModalContent from './AddUserModalContent';
import Modal from '../../shared/Modal';
import Loading from '../../shared/Loading';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import {
  getkycAll,
  deletekyc,
  frontEndBaseURL,
  getkycPDF,
  generateShareLink,
  getKycFormToCsv,
  createkyc,
  getkyc,
} from '../../../API/Api';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import FormStatusEnum from '../../../data/FormStatusEnum';
import 'react-toastify/dist/ReactToastify.css';
import { jwtDecode } from 'jwt-decode';
import { TokenContext } from '../../../App';

// 防抖 hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const _KycAmlCustomerListTable = () => {
  const currentLocation = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortDirection, setSortDirection] = useState('desc');
  const [collapsed, setCollapsed] = useState(false); // State for collapsing the table
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDuplicateAction, setPendingDuplicateAction] = useState(null);

  const [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  const userRole = decodedToken.role;
  const companyId = decodedToken.companyId;

  // 使用 ref 来避免无限循环
  const fetchingRef = useRef(false);

  // 使用防抖处理搜索词
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchData = React.useCallback(async () => {
    // 如果正在请求，直接返回
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;

    try {
      setLoading(true);

      // 构建 filters 对象
      const filters = {
        search: debouncedSearchTerm || undefined,
        sortBy: sortBy || undefined,
        sortOrder: sortDirection || 'desc',
      };

      const response = await getkycAll(companyId, null, page, pageSize, false, filters);
      // 兼容拦截器返回 undefined 的情况，或后端直接返回数组的情况
      const payload = response?.data ?? response;
      if (!payload) {
        console.error('Empty response from getkycAll');
        throw new Error('EMPTY_RESPONSE');
      }
      const allKycData = Array.isArray(payload) ? { data: payload } : payload;
      const activeKycData = [];

      allKycData.data.forEach(item => {
        // check how many days to next major rescreening
        const givenDate = DateTime.fromFormat(item.nextRescreeningDate || '27-06-2099', 'dd-MM-yyyy'); // default Date needed for results w/o rescreening result
        const diff = Math.round(givenDate.diff(DateTime.now(), 'days').days);

        let expiryStatus;
        if (diff < 0) {
          expiryStatus = 'expired';
        } else if (diff < 31) {
          expiryStatus = 'expiringSoon';
        }

        const details = item.details;
        if (!item.isExpiry && details) {
          if (item.type === 'individual') {
            if (details.identificationExpiryDate && details.identificationExpiryDate != '0') {
              try {
                const idExpiryDate = new Date(details.identificationExpiryDate);
                const daysToExpiry = (idExpiryDate.getTime() - new Date().getTime()) / (1000 * 3600 * 24);
                if (daysToExpiry <= 30) {
                  item.isExpiry = true;
                }
              } catch (err) {
                console.error(err);
              }
            }
          } else if (item.type === 'entity') {
            if (details.connectedParty) {
              for (let party of details.connectedParty) {
                if (party.identificationExpiryDate && party.identificationExpiryDate != '0') {
                  try {
                    const idExpiryDate = new Date(party.identificationExpiryDate);
                    const daysToExpiry = (idExpiryDate.getTime() - new Date().getTime()) / (1000 * 3600 * 24);
                    if (daysToExpiry <= 30) {
                      item.isExpiry = true;
                      break;
                    }
                  } catch (err) {
                    console.error(err);
                  }
                }
              }
            }
          }
        }

        if (!item.archived) {
          activeKycData.push({
            id: item._id,
            name: item.name,
            email: item.email,
            type: item.type,
            formAuthority: item.formAuthority,
            maxPage: item.maxPage,
            nextRescreeningDate: item.apiRefreshTime,
            nextAssessmentDate: item.nextRescreeningDate,
            submitedDate: item.submitedDate,
            companyId: item.companyId,
            status: expiryStatus || item.formStatus,
            formStatus: item.formStatus,
            reviewStatus: item.reviewStatus,
            riskLevel: item.riskLevel,
            isExpiry: item.isExpiry,
            approvalPerson: item.approvalPerson,
            reviewerReviewUser: item.reviewerReviewUser,
            createUserDetail: item.createUserDetail,
          });
        }
      });

      setUsers(activeKycData);

      // Update pagination info (keep page state independent)
      if (allKycData.pagination) {
        const backendTotal = allKycData.pagination.total;
        const backendPageSize = allKycData.pagination.pageSize || pageSize;
        const backendTotalPages = allKycData.pagination.totalPages || Math.ceil(backendTotal / backendPageSize);

        setPagination(prev => ({
          ...prev,
          page: page,
          pageSize: backendPageSize,
          total: backendTotal,
          totalPages: backendTotalPages,
          hasNextPage: allKycData.pagination.hasNextPage ?? page < backendTotalPages,
          hasPrevPage: allKycData.pagination.hasPrevPage ?? page > 1,
        }));
      } else {
        // If no pagination data from backend, use current page data count
        setPagination(prev => ({
          ...prev,
          page: page,
          pageSize: pageSize,
          total: activeKycData.length,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: page > 1,
        }));
      }

      await new Promise(r => setTimeout(r, 150));
    } catch (error) {
      console.error('Error fetching KYC data:', {
        message: error?.message,
        status: error?.response?.status,
        url: error?.config?.url,
        backend: error?.response?.data,
      });
      const backendMsg = error?.response?.data?.message || error?.message || 'Failed to fetch customer data';
      toast.error(backendMsg);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [companyId, page, pageSize, debouncedSearchTerm, sortBy, sortDirection]);

  // 当防抖后的搜索词变化时，重置到第一页（如果不在第一页）
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatSubmittedDate = dateString => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // If invalid date, return original

      // Format time: HH:mm
      const time = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

      // Format date: DD-MM-YYYY
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const dateStr = `${day}-${month}-${year}`;

      return (
        <div className='flex flex-col items-center'>
          <span className='text-base '>{dateStr}</span>
          <span className='text-xs text-gray-500'>{time}</span>
        </div>
      );
    } catch (error) {
      return dateString;
    }
  };

  const getRiskLevelTag = riskLevel => {
    if (!riskLevel) {
      return '-';
    } else if (riskLevel == 'Low') {
      return <label className='bg-green-200 text-black rounded-full px-2 py-1 text-xs font-semibold'>Low</label>;
    } else if (riskLevel == 'High') {
      return <label className='bg-red-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>High</label>;
    } else if (riskLevel == 'Pending') {
      return <label className='bg-yellow-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>Pending</label>;
    } else if (riskLevel == 'Medium') {
      return <label className='bg-orange-400 text-white rounded-full px-2 py-1 text-xs font-semibold'>Medium</label>;
    } else {
      return (
        <label className='bg-orange-400 text-white rounded-full px-2 py-1 text-xs font-semibold'>{riskLevel}</label>
      );
    }
  };

  const getStatusTag = (status, reviewStatus, formAuthority, details) => {
    // console.log(reviewStatus)
    if (status == FormStatusEnum.PENDING_CLIENT_DETAILS_1) {
      // Pending Client Details
      return <label className='bg-yellow-200 text-black rounded-full px-2 py-1 text-xs font-semibold'>PD</label>;
    } else if (status == FormStatusEnum.PENDING_RISK_ASSESSMENT_2) {
      // Pending Risk Assessment
      return <label className='bg-amber-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>RA</label>;
    } else if (status == FormStatusEnum.PENDING_ADMIN_REVIEW_3) {
      // Pending reviewer Review
      return <label className='bg-orange-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>PR</label>;
    } else if (status == 'expiringSoon') {
      return (
        <label className='bg-gray-400 text-white rounded-full px-2 py-1 text-xs font-semibold'>Expiring Soon</label>
      );
    } else if (status == 'expired') {
      return <label className='bg-black text-white rounded-full px-2 py-1 text-xs font-semibold'>Expired</label>;
    } else if (status == FormStatusEnum.ACCEPTED_4) {
      // if(formAuthority=='Law'){
      return (
        <span className='flex space-x-2'>
          <label className='bg-green-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>OM</label>
          {reviewStatus === 'Pending review' && (
            <label className='bg-blue-200 text-black rounded-full px-2 py-1 text-xs font-semibold'>PR</label>
          )}
          {reviewStatus === 'Reviewed' && (
            <label className='bg-purple-200 text-black rounded-full px-2 py-1 text-xs font-semibold'>R</label>
          )}
          {reviewStatus === 'Pending Assessment' && (
            <label className='bg-pink-200 text-black rounded-full px-2 py-1 text-xs font-semibold'>PA</label>
          )}
        </span>
      );
      // }else{
      //   // Ongoing Monitoring
      //   return <label className="bg-green-400 text-black rounded-full px-2 py-1 text-xs font-semibold">OM</label>
      // }
    } else if (status == FormStatusEnum.REJECTED_4) {
      return <label className='bg-red-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>Rejected</label>;
    }
  };

  const getStatusTagShow = (status, reviewStatus, formAuthority) => {
    // console.log(reviewStatus)
    if (status == FormStatusEnum.PENDING_CLIENT_DETAILS_1) {
      // Pending Client Details
      return <label className='p-3 w-3/12'>Pending Client Details</label>;
    } else if (status == FormStatusEnum.PENDING_RISK_ASSESSMENT_2) {
      // Pending Risk Assessment
      return <label className='p-3 w-3/12'>Pending Risk Assessment</label>;
    } else if (status == FormStatusEnum.PENDING_ADMIN_REVIEW_3) {
      // Pending reviewer Review
      return <label className='p-3 w-3/12'>Pending reviewer Review</label>;
    } else if (status == 'expiringSoon') {
      return <label className='p-3 w-3/12'>Expiring Soon</label>;
    } else if (status == 'expired') {
      return <label className='p-3 w-3/12'>Expired</label>;
    } else if (status == FormStatusEnum.ACCEPTED_4) {
      // if (formAuthority == 'Law') {
      return (
        <span className='flex'>
          {' '}
          {/* 移除 flex-wrap，保留 gap-2 以控制间距 */}
          <label className='p-2  min-w-[100px] whitespace-nowrap'>Ongoing Monitoring</label>
          {reviewStatus === 'Pending review' && (
            <label className='p-2  min-w-[100px] whitespace-nowrap'>Pending review</label>
          )}
          {reviewStatus === 'Reviewed' && <label className='p-2 min-w-[100px] whitespace-nowrap'>Reviewed</label>}
          {reviewStatus === 'Pending Assessment' && (
            <label className='p-2  min-w-[100px] whitespace-nowrap'>Pending Assessment</label>
          )}
        </span>
      );
      // } else {
      //   return <label className="p-2  min-w-[100px] whitespace-nowrap">Ongoing Monitoring</label>
      // }
    } else if (status == FormStatusEnum.REJECTED_4) {
      return <label className='bg-red-400 text-black rounded-full px-2 py-1 text-xs font-semibold'>Rejected</label>;
    }
  };

  const handleKycPDF = async (clientFormId, userName) => {
    const response = await getkycPDF(clientFormId); //  ({ responseType: 'blob' })

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const filename = `${userName}_Details_${DateTime.now().setZone('Asia/Singapore').toFormat('dd-MM-yyyy')}.pdf`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
  };

  const handleKycDownloadCsv = async (kycId, kycName) => {
    try {
      const response = await getKycFormToCsv(kycId);
      // Create a Blob from the array buffer
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `${decodedToken.companyId} Summary report - ${DateTime.now().toFormat('dd-MM-yyyy')}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:');
      toast.error('Download failed...');
    }
  };

  const getTypeLabel = typeVal => {
    let typeLabel = '-';
    switch (typeVal) {
      case 'individual':
        typeLabel = 'Individual';
        break;
      case 'entity':
        typeLabel = 'Entity';
        break;
      default:
        break;
    }
    return typeLabel;
  };

  const getCurrentReviewer = (formStatus, reviewerReviewUser, createUserDetail) => {
    if (formStatus === FormStatusEnum.PENDING_ADMIN_REVIEW_3) {
      // pending_reviewer_review 状态，显示 reviewerReviewUser
      return reviewerReviewUser || '-';
    } else {
      // 其他状态，显示 createUserDetail.username
      return createUserDetail?.username || '-';
    }
  };

  const copyFormLink = async (clientFormId, maxPage, companyId, user) => {
    if (userRole == 'viewer') {
      toast.info('Viewer cannot perform that action');
      return;
    }

    const formTokenData = {
      name: user.name,
      userId: clientFormId,
      companyId: companyId,
    };

    try {
      const shortLinkToCopy = await generateShareLink({
        formTokenData,
        frontEndBaseURL,
        clientFormId,
      });

      if (shortLinkToCopy && shortLinkToCopy.data) {
        await navigator.clipboard.writeText(shortLinkToCopy.data);
        toast.success('External Form Link Copied');
      } else {
        console.error('Failed to generate short link: empty response data', shortLinkToCopy);
        toast.error('Failed to generate share link, please try again');
      }
    } catch (error) {
      console.error('Error generating short link:', error);
      toast.error('Failed to generate share link, please try again');
    }
  };

  const handleDuplicateClick = (clientFormId, user) => {
    if (userRole == 'viewer') {
      toast.info('Viewer cannot perform that action');
      return;
    }

    // 保存待执行的操作
    setPendingDuplicateAction({ clientFormId, user });
    // 显示确认对话框
    setShowConfirmDialog(true);
  };

  const handleConfirmDuplicate = async () => {
    setShowConfirmDialog(false);

    if (!pendingDuplicateAction) {
      return;
    }

    const { clientFormId } = pendingDuplicateAction;
    setPendingDuplicateAction(null);

    try {
      // 检查API调用配额
      const quotaExceeded = await checkAPICallsQuota(setShowQuotaModal);
      if (quotaExceeded) {
        return;
      }

      // 获取完整的client数据
      const kycData = (await getkyc(clientFormId).then(response => response.data)) || {};

      // 创建复制的client数据，参考OnGoingMonitoring_Law.js的逻辑
      const duplicateKycData = {
        ...kycData,
        updatedDate: DateTime.now().toFormat('dd-MM-yyyy HH:mm:ss'),
        submitedDate: DateTime.now().toFormat('dd-MM-yyyy HH:mm:ss'),
        formStatus: FormStatusEnum.PENDING_CLIENT_DETAILS_1,
        maxPage: 1,
      };
      delete duplicateKycData._id;
      delete duplicateKycData.nextRescreeningDate;
      delete duplicateKycData.onGoingMonitoring;

      // 创建新的client
      const response = await createkyc(duplicateKycData);

      if (response && response.status === 200 && response.data.status) {
        toast.success('Case duplicated successfully');
        // 刷新列表
        fetchData();
      } else {
        toast.error('Failed to duplicate case');
      }
    } catch (error) {
      console.error('Error duplicating case:', error);
      toast.error('Failed to duplicate case, please try again');
    }
  };

  const handleCancelDuplicate = () => {
    setShowConfirmDialog(false);
    setPendingDuplicateAction(null);
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = e => {
    const newPageSize = parseInt(e.target.value, 10);
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
    setLoading(true); // Trigger data refetch
  };

  const handleArchive = async userId => {
    if (userRole == 'viewer') {
      toast.info('Viewer role cannot perform this action');
      return;
    }

    // Add confirmation dialog
    const isConfirmed = window.confirm(
      'Please confirm that you want to archive this file. This action is IRREVERSIBLE.'
    );

    if (!isConfirmed) {
      toast.info('Operation cancelled');
      return;
    }

    try {
      await deletekyc(userId);
      // Refresh data
      fetchData();
      toast.success('Client archived successfully');
    } catch (error) {
      console.error('Archive failed:', error);
      toast.error('Archive failed, please try again');
    }
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pageButtons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous page button
    pageButtons.push(
      <button
        key='prev'
        onClick={() => handlePageChange(pagination.page - 1)}
        disabled={!pagination.hasPrevPage}
        className='px-3 py-1 border rounded mx-1 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100'
      >
        Previous
      </button>
    );

    // First page button (if not visible)
    if (startPage > 1) {
      pageButtons.push(
        <button key={1} onClick={() => handlePageChange(1)} className='px-3 py-1 border rounded mx-1 hover:bg-gray-100'>
          1
        </button>
      );
      if (startPage > 2) {
        pageButtons.push(
          <span key='ellipsis1' className='px-2'>
            ...
          </span>
        );
      }
    }

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 border rounded mx-1 ${
            pagination.page === i ? 'bg-green-500 text-white' : 'hover:bg-gray-100'
          }`}
        >
          {i}
        </button>
      );
    }

    // Last page button (if not visible)
    if (endPage < pagination.totalPages) {
      if (endPage < pagination.totalPages - 1) {
        pageButtons.push(
          <span key='ellipsis2' className='px-2'>
            ...
          </span>
        );
      }
      pageButtons.push(
        <button
          key={pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
          className='px-3 py-1 border rounded mx-1 hover:bg-gray-100'
        >
          {pagination.totalPages}
        </button>
      );
    }

    // Next page button
    pageButtons.push(
      <button
        key='next'
        onClick={() => handlePageChange(pagination.page + 1)}
        disabled={!pagination.hasNextPage}
        className='px-3 py-1 border rounded mx-1 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100'
      >
        Next
      </button>
    );

    return pageButtons;
  };

  const handleDateSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const handleAssessmentDateSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const handleNameSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc'); // Default to ascending when a new column is selected
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const handleStatusSort = column => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  return (
    <div>
      <Row>
        {' '}
        {/*  Top menu Options */}
        <div className='w-full flex justify-between items-center p-4 bg-white shadow'>
          <strong>KYC reporting status</strong>
          <input
            type='text'
            placeholder='Search Name'
            value={searchTerm}
            onChange={handleSearchChange}
            className='p-2 border border-gray-300 rounded-lg mr-2 w-1/3'
          />
          <Modal title='Add Customer'>
            <AddUserModalContent setLoading={() => fetchData()} />
          </Modal>

          <Link to='/kyc-aml/archived-customer-list' replace={currentLocation.pathname === '/'}>
            <button className='bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg'>
              View Archives
            </button>
          </Link>

          <button
            onClick={handleKycDownloadCsv}
            className='bg-green-400 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg'
          >
            Download All
          </button>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className='bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-2 rounded-full'
          >
            <BsChevronDown />
          </button>
        </div>
      </Row>
      {!collapsed && (
        <div className='relative'>
          {loading && users.length === 0 ? (
            <Loading message='Loading KYC data...' />
          ) : (
            <div className='relative'>
              {loading && <Loading message='Loading...' />}
              <table className='w-full border-collapse border border-gray-300 shadow table-auto'>
                <thead>
                  <tr className='bg-gray-300 text-left'>
                    <th className='p-3 w-2/12 cursor-pointer' onClick={() => handleNameSort('name')}>
                      {' '}
                      Name{' '}
                      <span className='pl-2 pt-1'>
                        {' '}
                        <BsChevronDown className='inline-block' />{' '}
                      </span>{' '}
                    </th>
                    {/* <th className="p-2 w-2/12 cursor-pointer text-center" onClick={() => handleDateSort('nextRescreeningDate')}> Next Screening Date  <span className='pl-2 pt-1 inline-block'> <BsChevronDown /> </span> <BsChevronDown className={`${Menu.isOpen && 'rotate-180'}`} /> </th> */}
                    <th className='p-2 w-2/12 text-center'>
                      <div className='flex flex-col items-center'>
                        <span>Submitted</span>
                        <span>Date</span>
                      </div>
                    </th>
                    <th
                      className='p-2 w-2/12 cursor-pointer text-center'
                      onClick={() => handleAssessmentDateSort('nextAssessmentDate')}
                    >
                      <div className='flex flex-col items-center'>
                        <div className='flex items-center gap-1'>
                          <span>Next</span>
                          <BsChevronDown className='text-xs' />
                        </div>
                        <span>Assessment Date</span>
                      </div>
                    </th>
                    <th className='p-2 w-1/12 text-center'>Type</th>
                    <th className='p-2 w-1/12 text-center'>Risk level</th>
                    <th className='p-2 w-2/12 cursor-pointer text-center' onClick={() => handleStatusSort('status')}>
                      {' '}
                      Status{' '}
                      <span className='pl-2 pt-1'>
                        {' '}
                        <BsChevronDown className='inline-block' />{' '}
                      </span>{' '}
                    </th>
                    <th className='p-2 w-2/12 text-center'>Case Owner</th>
                    <th className='p-2 w-1/12 text-center'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr
                      key={user.id}
                      className={index % 2 === 0 ? 'bg-green-50 hover:bg-green-100' : 'bg-white hover:bg-green-100'}
                    >
                      <td className='p-3 w-2/12'>
                        <Link
                          to={`/kyc-aml/form?clientFormId=${user.id}&page=${user.maxPage}`}
                          replace={
                            currentLocation.pathname ===
                            `/kyc-aml/overview-assessment?clientFormId=${user.id}&page=${user.maxPage}`
                          }
                        >
                          <Tooltip
                            title={
                              <p style={{ fontSize: '14px' }}>
                                {' '}
                                Edit {user.type}, {user.name}{' '}
                              </p>
                            }
                            className='border-stone-500'
                            arrow
                          >
                            {/* <Tooltip title={<p style={{ fontSize: "14px" }}> Edit {user.type}, {user.name}, {user.formAuthority} </p>} className='border-stone-500' arrow> -> ADHOC TEMP REMOVAL OF MAS DISPLAY (.formAuthority), TO UNCOMMENT AND REMOVE THE ABOVE LINE */}
                            <span className='cursor-pointer hover:underline'>{user.name}</span>
                          </Tooltip>
                        </Link>
                      </td>
                      <td className='p-2 w-2/12 text-center'>{formatSubmittedDate(user.submitedDate)}</td>
                      <td className='p-2 w-2/12 text-center'>{user.nextAssessmentDate || '-'}</td>
                      <td className='p-2 w-1/12 text-center'>{getTypeLabel(user.type)}</td>
                      <td className='p-2 w-1/12 text-xs text-center'>{getRiskLevelTag(user.riskLevel)}</td>
                      <td className='p-2 w-2/12 text-xs text-center'>
                        <Tooltip
                          title={
                            <div style={{ fontSize: '14px', width: 'fit-content' }}>
                              <span className='w-5/12'>
                                {getStatusTagShow(user.status, user.reviewStatus, user.formAuthority, user.isExpiry)}
                              </span>
                              {user.isExpiry && (
                                <span>
                                  <label className='p-3 w-5/12'>Document.Expired</label>
                                </span>
                              )}
                            </div>
                          }
                          className='border-stone-500 w-10/12'
                          arrow
                        >
                          {/* <Tooltip title={<p style={{ fontSize: "14px" }}> Edit {user.type}, {user.name}, {user.formAuthority} </p>} className='border-stone-500' arrow> -> ADHOC TEMP REMOVAL OF MAS DISPLAY (.formAuthority), TO UNCOMMENT AND REMOVE THE ABOVE LINE */}
                          <div className='flex space-x-2' style={{ justifyContent: 'center', alignItems: 'center' }}>
                            <span>{getStatusTag(user.status, user.reviewStatus, user.formAuthority)}</span>
                            {user.isExpiry && (
                              <span>
                                <label className='bg-black text-white rounded-full px-2 py-1 text-xs font-semibold'>
                                  DE
                                </label>
                              </span>
                            )}
                          </div>
                        </Tooltip>
                      </td>
                      <td className='p-2 w-1/12 text-center'>
                        {getCurrentReviewer(user.formStatus, user.reviewerReviewUser, user.createUserDetail)}
                      </td>
                      <td className='p-2 w-1/12 text-center'>
                        <div className='flex'>
                          <Tooltip
                            title={'Copy Form Link'}
                            className='p-2.5 mx-1 rounded-full border border-stone-500 cursor-pointer hover:bg-gray-400  transition duration-500'
                            arrow
                          >
                            <div onClick={() => copyFormLink(user.id, user.maxPage, user.companyId, user)}>
                              <BsLink45Deg />
                            </div>
                          </Tooltip>
                          <Tooltip
                            title={'Duplicate Case'}
                            className='p-2.5 mx-1 rounded-full border border-stone-500 cursor-pointer hover:bg-gray-400  transition duration-500'
                            arrow
                          >
                            <div onClick={() => handleDuplicateClick(user.id, user)}>
                              <BsCopy />
                            </div>
                          </Tooltip>
                          <Tooltip
                            title={'Download'}
                            className='p-2.5 mx-1 rounded-full cursor-pointer hover:bg-gray-400 border border-stone-500 transition duration-500'
                            arrow
                          >
                            <div onClick={() => handleKycPDF(user.id, user.name)}>
                              <BsDownload />
                            </div>
                          </Tooltip>
                          <Tooltip
                            title={'Archive'}
                            className='p-2.5 mx-1 rounded-full cursor-pointer hover:bg-gray-400 border border-stone-500 transition duration-500'
                            arrow
                          >
                            <div onClick={() => handleArchive(user.id)}>
                              <BsArchive />
                            </div>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className='flex justify-between items-center mt-4 p-4 bg-white'>
                  <div className='flex items-center gap-4'>
                    <div className='text-sm text-gray-600'>
                      Showing {(pagination.page - 1) * pagination.pageSize + 1} -{' '}
                      {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total} records
                    </div>
                    <div className='flex items-center gap-2'>
                      <label htmlFor='pageSize' className='text-sm text-gray-600'>
                        Show:
                      </label>
                      <select
                        id='pageSize'
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        className='border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                        disabled={loading}
                      >
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className='text-sm text-gray-600'>per page</span>
                    </div>
                  </div>
                  <div className='flex items-center'>{renderPagination()}</div>
                </div>
              )}
              {/* Show page size selector even when there's only one page */}
              {pagination.totalPages <= 1 && pagination.total > 0 && (
                <div className='flex justify-end items-center mt-4 p-4 bg-white'>
                  <div className='flex items-center gap-2'>
                    <label htmlFor='pageSizeSingle' className='text-sm text-gray-600'>
                      Show:
                    </label>
                    <select
                      id='pageSizeSingle'
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      className='border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                      disabled={loading}
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className='text-sm text-gray-600'>per page</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <br /> <br />
      <KycQuotaModal
        status={showQuotaModal}
        onClose={() => setShowQuotaModal(false)}
      />
      <Dialog open={showConfirmDialog} onClose={handleCancelDuplicate}>
        <div className='p-6 min-w-[400px]'>
          <div className='flex justify-between items-start mb-4'>
            <h2 className='text-lg font-semibold text-green-600'>Duplicate Case</h2>
            <Button onClick={handleCancelDuplicate} className='min-w-0 p-1 text-gray-500 hover:text-gray-700'>
              ×
            </Button>
          </div>
          <div className='mb-6'>
            <p className='text-gray-700 leading-relaxed'>
              This is a duplicate case. Duplicating will initiate a new screening count during the risk assessment
              stage.
            </p>
          </div>
          <div className='flex justify-end gap-3'>
            <button
              onClick={handleCancelDuplicate}
              className='px-6 py-2 border-2 border-green-500 text-green-600 bg-white hover:bg-green-50 hover:border-green-600 rounded font-semibold transition-colors'
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDuplicate}
              className='px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded font-semibold transition-colors'
            >
              Continue
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default _KycAmlCustomerListTable;
